import isEqual from "lodash/isEqual";
import type {
  IApiResponse,
  UserFolderSerializer,
  FolderReactionEnum,
  PaginationResponse_ArtShortSerializer_,
  DataPaginationResponse_ArtShortSerializer_,
} from "@litres-libs/types";

import { rtkqApi } from "../api/rtkqApi";
import { TagsType } from "../constants";
import type { RootState } from "../../store";
import {
  patchArtAddToFolder,
  patchArtRemoveFromFolder,
} from "../userArts/utils";

export const foldersApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getFoldersList: build.query<IApiResponse<UserFolderSerializer[]>, void>({
      query: () => ({ url: "/foundation/api/users/me/folders" }),
      providesTags: result =>
        result?.payload
          ? [
              ...result.payload.data.map(({ folder_id: folderId }) => ({
                type: TagsType.FOLDERS,
                id: folderId,
              })),
              { type: TagsType.FOLDERS, id: "LIST" },
            ]
          : [{ type: TagsType.FOLDERS, id: "LIST" }],
    }),
    getFolderArts: build.query<
      DataPaginationResponse_ArtShortSerializer_,
      { folderId: number; limit?: number; after?: string }
    >({
      query: ({ folderId, limit, after }) => ({
        url: `/foundation/api/folders/${folderId}/arts`,
        params: {
          limit,
          after,
        },
      }),
      transformResponse: (response: PaginationResponse_ArtShortSerializer_) => {
        return response.payload;
      },
      providesTags: (result, _err, { folderId }) =>
        result
          ? [
              ...result.data.map(({ id }) => ({
                type: TagsType.ART_SHORT_LIST as const,
                id,
              })),
              ...result.data.map(({ id }) => ({
                type: TagsType.MY_BOOKS as const,
                id,
              })),
              TagsType.ART_SHORT_LIST,
              { type: TagsType.FOLDERS, id: folderId },
            ]
          : [
              { type: TagsType.FOLDERS, id: folderId },
              TagsType.ART_SHORT_LIST,
              TagsType.MY_BOOKS,
            ],
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        return `${endpointName}:${queryArgs.folderId}`;
      },
      forceRefetch({ currentArg, previousArg }) {
        return !isEqual(currentArg, previousArg);
      },
      merge: (currentCache, newResponse, { arg }) => {
        if (arg.after) {
          // при ручной инвалидации кэша арты могут дублироваться, поэтому добавлена фильтрация по id
          const newData = newResponse.data.filter(
            item => !currentCache.data.find(current => current.id === item.id)
          );
          currentCache.data.push(...newData);
        } else {
          currentCache.data = newResponse.data;
        }

        currentCache.pagination = newResponse.pagination;
      },
    }),
    getFolder: build.query<UserFolderSerializer, { folderId: number }>({
      query: ({ folderId }) => ({ url: `/foundation/api/folders/${folderId}` }),
      providesTags: (result, error, { folderId }) => [
        { type: TagsType.FOLDERS, id: folderId },
      ],
      transformResponse: (response: IApiResponse<UserFolderSerializer>) => {
        return response.payload.data;
      },
    }),
    addFolder: build.mutation<
      IApiResponse<UserFolderSerializer>,
      { title: string }
    >({
      query: body => ({
        url: "/foundation/api/users/me/folders",
        method: "POST",
        body,
      }),
      invalidatesTags: [
        { type: TagsType.FOLDERS, id: "LIST" },
        { type: TagsType.MY_BOOKS, id: "STATS" },
      ],
    }),
    deleteFolder: build.mutation<IApiResponse<object>, { folderId: number }>({
      query({ folderId }) {
        return {
          url: `/foundation/api/users/me/folders/${folderId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [
        { type: TagsType.FOLDERS, id: "LIST" },
        { type: TagsType.MY_BOOKS, id: "STATS" },
      ],
    }),
    renameFolder: build.mutation<void, { folderId: number; title: string }>({
      query({ folderId, title }) {
        return {
          url: `/foundation/api/users/me/folders/${folderId}`,
          method: "PATCH",
          body: {
            title,
          },
        };
      },
      invalidatesTags: (result, error, { folderId }) => [
        { type: TagsType.FOLDERS, id: folderId },
      ],
    }),
    addToFolder: build.mutation<void, { folderId: number; artIds: number[] }>({
      query({ folderId, artIds }) {
        return {
          url: `/foundation/api/users/me/folders/${folderId}/add-arts`,
          method: "POST",
          body: {
            art_ids: artIds,
          },
        };
      },
      invalidatesTags: (result, error, { folderId }) => [
        { type: TagsType.FOLDERS, id: folderId },
      ],
    }),
    removeFromFolder: build.mutation<
      void,
      { artIds: number[]; folderId?: number }
    >({
      query: ({ artIds, folderId }) => {
        let url = `/foundation/api/users/me/folders`;
        if (folderId) {
          url += `/${folderId}`;
        }
        return {
          url: `${url}/remove-arts`,
          method: "POST",
          body: { art_ids: artIds },
        };
      },
      invalidatesTags: (result, error, { folderId }) => [
        { type: TagsType.FOLDERS, id: folderId || "LIST" },
      ],
    }),
    reFetchAfterAdd: build.mutation<
      null,
      {
        artId: number;
        folderIds: number[];
      }
    >({
      queryFn: () => ({ data: null }),
      invalidatesTags: (result, error, { artId, folderIds }) => [
        ...folderIds.map(folderId => ({
          type: TagsType.FOLDERS,
          id: folderId,
        })),
        { type: TagsType.MY_BOOKS, id: artId },
      ],
      onQueryStarted({ artId, folderIds }, { dispatch, getState }) {
        const state = getState() as RootState;
        const entries = rtkqApi.util.selectInvalidatedBy(state, [
          { type: TagsType.MY_BOOKS, id: artId },
        ]);
        entries.map(entry =>
          patchArtAddToFolder(artId, folderIds, dispatch, entry)
        );
      },
    }),
    reFetchAfterRemove: build.mutation<
      null,
      {
        artId: number;
        folderIds: number[];
      }
    >({
      queryFn: () => ({ data: null }),
      invalidatesTags: (result, error, { artId, folderIds }) => [
        ...folderIds.map(folderId => ({
          type: TagsType.FOLDERS,
          id: folderId,
        })),
        { type: TagsType.MY_BOOKS, id: artId },
      ],
      onQueryStarted({ artId, folderIds }, { dispatch, getState }) {
        const state = getState() as RootState;
        const entries = rtkqApi.util.selectInvalidatedBy(state, [
          { type: TagsType.MY_BOOKS, id: artId },
        ]);
        entries.map(entry =>
          patchArtRemoveFromFolder(artId, folderIds, dispatch, entry)
        );
      },
    }),

    addLikeToFolder: build.mutation<void, { folderId: number }>({
      query: ({ folderId }) => {
        return {
          url: `/foundation/api/folders/${folderId}/likes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedFolder = dispatch(
          foldersApi.util.updateQueryData("getFolder", params, draftFolder => {
            const isFolderDislikedByUser =
              draftFolder.user_reaction === "dislike";
            Object.assign<UserFolderSerializer, Partial<UserFolderSerializer>>(
              draftFolder,
              {
                user_reaction: "like" as FolderReactionEnum,
                likes_count: draftFolder.likes_count + 1,
                ...(isFolderDislikedByUser && {
                  dislikes_count: draftFolder.dislikes_count - 1,
                }),
              }
            );
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedFolder.undo();
        }
      },
    }),

    removeLikeFromFolder: build.mutation<void, { folderId: number }>({
      query: ({ folderId }) => {
        return {
          url: `/foundation/api/folders/${folderId}/likes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedFolder = dispatch(
          foldersApi.util.updateQueryData("getFolder", params, draftFolder => {
            Object.assign<UserFolderSerializer, Partial<UserFolderSerializer>>(
              draftFolder,
              {
                // TODO Выпилить кастинг после перехода на union
                user_reaction: "no_reaction" as FolderReactionEnum,
                likes_count: draftFolder.likes_count - 1,
              }
            );
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedFolder.undo();
        }
      },
    }),

    addDislikeToFolder: build.mutation<string, { folderId: number }>({
      query: ({ folderId }) => {
        return {
          url: `/foundation/api/folders/${folderId}/dislikes`,
          method: "PUT",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedFolder = dispatch(
          foldersApi.util.updateQueryData("getFolder", params, draftFolder => {
            const isFolderLikedByUser = draftFolder.user_reaction === "like";
            Object.assign<UserFolderSerializer, Partial<UserFolderSerializer>>(
              draftFolder,
              {
                user_reaction: "dislike" as FolderReactionEnum,
                dislikes_count: draftFolder.dislikes_count + 1,
                ...(isFolderLikedByUser && {
                  likes_count: draftFolder.likes_count - 1,
                }),
              }
            );
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedFolder.undo();
        }
      },
    }),

    removeDislikeFromFolder: build.mutation<string, { folderId: number }>({
      query: ({ folderId }) => {
        return {
          url: `/foundation/api/folders/${folderId}/dislikes`,
          method: "DELETE",
        };
      },
      async onQueryStarted(params, { dispatch, queryFulfilled }) {
        const optimisticPatchedFolder = dispatch(
          foldersApi.util.updateQueryData("getFolder", params, draftFolder => {
            Object.assign<UserFolderSerializer, Partial<UserFolderSerializer>>(
              draftFolder,
              {
                user_reaction: "no_reaction" as FolderReactionEnum,
                dislikes_count: draftFolder.dislikes_count - 1,
              }
            );
          })
        );
        try {
          await queryFulfilled;
        } catch (e) {
          optimisticPatchedFolder.undo();
        }
      },
    }),
  }),
});
